import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import { FC, useLayoutEffect, useRef, useState } from 'react';
import * as React from 'react';
import mediaQueries from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { mainColor, secondaryColor } from 'src/constants/stylesheet';
import ViewMonitor from 'src/utils/ViewMonitor';
import PanelImages from './Panels/PanelImages';
import PanelLightbox from './Panels/PanelLightbox';
import PanelNewsletter from './Panels/PanelNewsletter';
import PanelRelatedExhibitions from './Panels/PanelRelatedExhibitions';
import PanelText from './Panels/PanelText';
import PanelTextImage from './Panels/PanelTextImage';
import PanelTwoColumnsText from './Panels/PanelTwoColumnsText';
import PanelVideo from './Panels/PanelVideo';

const useStyles = makeStyles(() =>
  createStyles({
    panelsContent: {
      overflow: 'hidden',
      transition: 'height .4s ease',

      [mediaQueries.Mobile]: {
        width: `100%`,
      },
      [mediaQueries.Tablet]: {
        width: `${2 / 3 * 100}%`,
      },
      [mediaQueries.Laptop]: {
        width: `${2 / 3 * 100}%`,
      },
      [mediaQueries.Desktop]: {
        width: `${2 / 3 * 100}%`,
      },
      [mediaQueries.BigDesktop]: {
        width: `${3 / 4 * 100}%`,
      },

      '&$panelsContentBig': {
        width: `100%`,
      }
    },
    panelsContentBig: {},
    visiblePanels: {
      '$panelsContentBig &': {
        display: 'none'
      },
      [mediaQueries.Mobile]: {
        paddingBottom: spacing.Gutter.Mobile / 2,
      },
      [mediaQueries.Tablet]: {
        paddingBottom: spacing.Gutter.Tablet / 2,
      },
      [mediaQueries.Laptop]: {
        paddingBottom: spacing.Gutter.Laptop / 2,
      },
      [mediaQueries.Desktop]: {
        paddingBottom: spacing.Gutter.Desktop / 2,
      },
      [mediaQueries.BigDesktop]: {
        paddingBottom: spacing.Gutter.BigDesktop / 2,
      }
    },
    hiddenPanels: {

      [mediaQueries.Mobile]: {
        paddingBottom: spacing.Gutter.Mobile / 2,
      },
      [mediaQueries.Tablet]: {
        paddingBottom: spacing.Gutter.Tablet / 2,
      },
      [mediaQueries.Laptop]: {
        paddingBottom: spacing.Gutter.Laptop / 2,
      },
      [mediaQueries.Desktop]: {
        paddingBottom: spacing.Gutter.Desktop / 2,
      },
      [mediaQueries.BigDesktop]: {
        paddingBottom: spacing.Gutter.BigDesktop / 2,
      }
    },
    readMore: {
      position: 'relative',
      width: '100%',
      color: secondaryColor,
      ...stylesheet.BoldText,
      paddingBottom: 20,

      '&$open': {
        display: 'none'
      },

      '& span': {
        cursor: 'pointer'
      },

      '&:hover': {
        color: mainColor
      }
    },
    readMorePadding: {
      [mediaQueries.Mobile]: {
        paddingLeft: spacing.GutterGrid.Mobile / 2,
        paddingRight: spacing.GutterGrid.Mobile / 2,
      },
      [mediaQueries.Tablet]: {
        paddingLeft: spacing.GutterGrid.Tablet / 2,
        paddingRight: spacing.GutterGrid.Tablet / 2,
      },
      [mediaQueries.Laptop]: {
        paddingLeft: spacing.GutterGrid.Laptop / 2,
        paddingRight: spacing.GutterGrid.Laptop / 2,
      },
      [mediaQueries.Desktop]: {
        paddingLeft: spacing.GutterGrid.Desktop / 2,
        paddingRight: spacing.GutterGrid.Desktop / 2,
      },
      [mediaQueries.BigDesktop]: {
        width: `${2 / 3 * 100}%`,
        marginLeft: 'auto',
        paddingLeft: spacing.GutterGrid.BigDesktop / 2,
        paddingRight: spacing.GutterGrid.BigDesktop / 2,
      }
    },
    open: {},
    hide: {
      ...stylesheet.Hide
    },
    show: {
      ...stylesheet.Show
    },
  }));

export type Panels = {
  visiblePanels: IDto[];
  hiddenPanels?: IDto[];
  panelReadMore?: JSX.Element;
};

type Props = Panels;
export const getWindowSize = () => {
  const [size, setSize] = useState([0, 0]);

  useLayoutEffect(() => {
    const updateSize = () => {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

const Panels: FC<Props> = ({ visiblePanels, hiddenPanels, panelReadMore }) => {
  const cls = useStyles();

  const parentPanelsRef = useRef<HTMLDivElement>(null);
  const visiblePanelsRef = useRef<HTMLDivElement>(null);
  const hiddenPanelsRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [visiblePanelsHeight, setVisiblePanelsHeight] = useState<number>();
  const windSize = getWindowSize();

  // TODO: how can we do better?
  React.useEffect(() => {
    const timer = window.setInterval(() => {
      if (!parentPanelsRef.current || !visiblePanelsRef.current) { return }
      setVisiblePanelsHeight(visiblePanelsRef.current.clientHeight);
    }, 50);

    return () => window.clearInterval(timer);
  }, []);

  React.useEffect(() => {
    if (!parentPanelsRef.current || !visiblePanelsRef.current || show) { return }
    const visibleHeight = visiblePanelsRef.current.clientHeight;
    if (!open) {
      parentPanelsRef.current.style.height = `${visibleHeight}px`;
      return;
    }

    if (hiddenPanelsRef.current) {
      const hiddenPanelsHeight = hiddenPanelsRef.current.clientHeight;
      parentPanelsRef.current.style.height = `${visibleHeight + hiddenPanelsHeight}px`;
    }

  }, [open, windSize, visiblePanelsHeight]);

  return (
    <div className={classNames(cls.panelsContent, show && cls.panelsContentBig)} ref={parentPanelsRef}>
      <div className={cls.visiblePanels} ref={visiblePanelsRef}>
        {
          visiblePanels.map((panel, i) => {
            switch (panel.type) {

              case "PanelText":
                const panelText = panel as PanelTextDto;
                return <PanelText {...panelText} key={`panel-text-${i}`} />

              case "PanelImages":
                const panelImages = panel as PanelImagesDto;
                return <PanelImages {...panelImages} key={`panel-images-${i}`} />

              case "PanelVideo":
                const panelVideo = panel as PanelVideoDto;
                return <PanelVideo {...panelVideo} key={`panel-video-${i}`} />

              case "PanelNewsletter":
                const panelNewsletter = panel as PanelNewsletterDto;
                return <PanelNewsletter {...panelNewsletter} key={`panel-newsletter-${i}`} />

              case "PanelTwoColumnsText":
                const panelTwoColumns = panel as PanelTwoColumnsTextDto;
                return <PanelTwoColumnsText {...panelTwoColumns} key={`panel-texts-${i}`} />

              case "PanelTextImage":
                const panelTextImage = panel as PanelTextImageDto;
                return <PanelTextImage {...panelTextImage} key={`panel-text-image-${i}`} />

              default:
                return null;
            }
          })
        }
        {panelReadMore && <ViewMonitor once={true}>{(intersect) =>
          <div className={
            classNames(cls.readMore, cls.readMorePadding, cls.hide, intersect && cls.show, open && cls.open)}>
            <span
              // tslint:disable-next-line:jsx-no-lambda
              onClick={() => setOpen(!open)}
            >
              {panelReadMore}
            </span>
          </div>
        }</ViewMonitor>}
      </div>
      {hiddenPanels &&
        <div className={cls.hiddenPanels} ref={hiddenPanelsRef}>
          {
            hiddenPanels.map((panel, i) => {
              switch (panel.type) {

                case "PanelText":
                  const panelText = panel as PanelTextDto;
                  return <PanelText {...panelText} key={`panel-text-${i}`} />

                case "PanelImages":
                  const panelImages = panel as PanelImagesDto;
                  return <PanelImages {...panelImages} key={`panel-images-${i}`} />

                case "PanelVideo":
                  const panelVideo = panel as PanelVideoDto;
                  return <PanelVideo {...panelVideo} key={`panel-video-${i}`} />

                case "PanelNewsletter":
                  const panelNewsletter = panel as PanelNewsletterDto;
                  return <PanelNewsletter {...panelNewsletter} key={`panel-newsletter-${i}`} />

                case "PanelTwoColumnsText":
                  const panelTwoColumns = panel as PanelTwoColumnsTextDto;
                  return <PanelTwoColumnsText {...panelTwoColumns} key={`panel-texts-${i}`} />

                case "PanelTextImage":
                  const panelTextImage = panel as PanelTextImageDto;
                  return <PanelTextImage {...panelTextImage} key={`panel-text-image-${i}`} />

                default:
                  return null;
              }
            })
          }
        </div>
      }
      {
        visiblePanels.map((panel, i) => {
          switch (panel.type) {

            case "PanelLightbox":
              if (!show) { setShow(true); }
              const panelLightbox = panel as PanelLightboxDto;
              return <PanelLightbox {...panelLightbox} key={`panel-lightbox-${i}`} />

            case "PanelRelatedExhibitions":
              if (!show) { setShow(true); }
              const panelRelatedExhibitions = panel as PanelRelatedExhibitionsDto;
              return <PanelRelatedExhibitions {...panelRelatedExhibitions} key={`panel-related-exhibitions-${i}`} />

            default:
              return null;
          }
        })
      }
    </div>
  );
};

export default Panels;


// WEBPACK FOOTER //
// ./src/Components/Panels.tsx